import { getStorageImageUrl } from "@shared/lib/image-utils";
import type { ShopperAppearance } from "~/server/shopper";

export type CustomFont = NonNullable<ShopperAppearance["customFont"]>;

/** Arabic script keeps IBM Plex Sans Arabic, as with the preset typefaces. */
const FALLBACK = '"IBM Plex Sans Arabic", ui-sans-serif, system-ui, sans-serif';

/**
 * The name the font is declared under: from its file id, never the retailer's family name,
 * so it cannot clash with a font installed on the device and no free text reaches the CSS.
 */
function declaredName(font: CustomFont): string {
	return `brand-${font.path
		.split("/")
		.at(-1)
		?.replace(/\.woff2$/, "")}`;
}

export function customFontUrl(font: CustomFont): string {
	return getStorageImageUrl(font.path) ?? "";
}

/** The CSS font-family stack for a retailer's uploaded typeface. */
export function customFontStack(font: CustomFont): string {
	return `"${declaredName(font)}", ${FALLBACK}`;
}

/**
 * Declares the retailer's uploaded typeface (`appearance.customFont`), served by the image
 * proxy, with `font-display: swap` so text shows in the fallback while it loads, and makes it
 * the typeface of the chat `scope` selects, over the presets. Built only from the
 * schema-checked storage path. Goes after the theme (`shopperThemeCss`) in the same sheet.
 */
export function customFontCss(font: CustomFont, scope: string): string {
	const stack = customFontStack(font);
	return `@font-face{font-family:"${declaredName(font)}";src:url("${customFontUrl(font)}") format("woff2");font-display:swap}${scope}{--font-sans:${stack};font-family:${stack}}`;
}
